import { Injectable, inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Subject } from 'rxjs';

import { withCurrentLocale } from './keycloak-locale';

/**
 * Marge, en secondes, sous laquelle le jeton est renouvelé avant de partir :
 * une requête émise à 29 s de l'échéance n'arrive pas expirée au serveur.
 */
export const TOKEN_MIN_VALIDITY_SECONDS = 30;

/** La session Keycloak est close : le jeton n'a pas pu être renouvelé. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expirée');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Le jeton d'accès, tenu à jour.
 *
 * <p><b>Pourquoi ce service existe.</b> `KeycloakService.getToken()` rend le
 * jeton en mémoire, tel quel : il ne le renouvelle pas. Le renouvellement de
 * `keycloak-angular` vit dans son intercepteur de classe, que cette application
 * n'enregistre pas — elle a le sien, fonctionnel. Le jeton du démarrage servait
 * donc jusqu'à son expiration, et avec lui les droits du démarrage : un profil
 * attribué entre-temps n'apparaissait qu'à la reconnexion.</p>
 *
 * <p>Désormais chaque requête vers l'API demande un jeton encore valable
 * {@link TOKEN_MIN_VALIDITY_SECONDS} secondes ; sinon il est renouvelé d'abord.
 * Un jeton renouvelé porte les droits du moment : s'ils ont changé,
 * {@link rightsChanged$} le dit, et le menu se recompose.</p>
 *
 * <p><b>Un seul renouvellement à la fois.</b> Un écran lance souvent trois
 * lectures d'un coup : elles attendent la même promesse.</p>
 *
 * <p><b>Pas de boucle à l'échec.</b> Deux échecs distincts :
 * <ul>
 *   <li>Keycloak <i>refuse</i> (session close, jeton de renouvellement
 *       expiré) : `keycloak-js` vide alors le jeton. La requête n'est pas
 *       émise, et la reconnexion est lancée <b>une fois</b> — les requêtes
 *       suivantes échouent sans la relancer.</li>
 *   <li>Keycloak est <i>injoignable</i> : la session existe peut-être encore.
 *       La requête part avec le jeton qu'on a ; s'il est expiré l'API répond
 *       401 et l'écran affiche son erreur. Rediriger vers un serveur
 *       d'authentification injoignable ferait perdre la page pour rien.</li>
 * </ul></p>
 */
@Injectable({ providedIn: 'root' })
export class SessionTokenService {
  private readonly keycloak = inject(KeycloakService);

  private readonly changed = new Subject<void>();
  /** Émet quand un renouvellement a rapporté d'autres rôles ou permissions. */
  readonly rightsChanged$ = this.changed.asObservable();

  private pending: Promise<string | undefined> | null = null;
  private knownRights: string | null = null;
  private reconnecting = false;

  /** Le jeton à poser sur la requête — renouvelé s'il allait expirer. */
  current(): Promise<string | undefined> {
    this.pending ??= this.renew().finally(() => (this.pending = null));
    return this.pending;
  }

  private async renew(): Promise<string | undefined> {
    const kc = this.keycloak.getKeycloakInstance();

    // Session anonyme (démarrage sans Keycloak, page publique) : rien à
    // renouveler, et pas de reconnexion forcée — les gardes de route s'en chargent.
    if (!kc?.authenticated || typeof kc.updateToken !== 'function') {
      return this.keycloak.getToken();
    }

    this.knownRights ??= this.rights();

    try {
      // L'instance et non `KeycloakService.updateToken` : celui-ci avale
      // l'échec et rend `false`, qui veut aussi dire « rien à renouveler ».
      const refreshed = await kc.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
      if (refreshed) this.noticeRights();
    } catch {
      if (!kc.authenticated || !kc.token) {
        this.reconnect();
        throw new SessionExpiredError();
      }
    }

    return kc.token;
  }

  private noticeRights(): void {
    const now = this.rights();
    if (now === this.knownRights) return;
    this.knownRights = now;
    this.changed.next();
  }

  /** Empreinte de ce que le jeton ouvre : rôles de realm et rôles de client. */
  private rights(): string {
    const parsed = this.keycloak.getKeycloakInstance()?.tokenParsed as
      | { realm_access?: { roles?: string[] }; resource_access?: Record<string, { roles?: string[] }> }
      | undefined;
    const realm = parsed?.realm_access?.roles ?? [];
    const clients = Object.values(parsed?.resource_access ?? {}).flatMap((c) => c?.roles ?? []);
    return [...realm, ...clients].sort().join('|');
  }

  private reconnect(): void {
    if (this.reconnecting) return;
    this.reconnecting = true;
    // La page s'en va : le drapeau n'est rendu que si la redirection échoue.
    Promise.resolve(this.keycloak.login(withCurrentLocale({ redirectUri: window.location.href })))
      .catch(() => (this.reconnecting = false));
  }
}
